import { useSetupStore } from '../../store';
import { TemplateSelect } from '../template-select';
import { DomainSelect } from '../domain-select';
import { FileSelect } from '../file-select';
import { PluginView } from '../plugin-view';
import { useStore } from '@/store';
import {
  useFetchOperatorIdByUserId,
  useDomainsbyOperatorId,
  useLanguagebyOperatorId,
} from '@/api';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { useNotify } from '@/hooks';
import { assertNotEmpty } from '@morph-mapper/utils';
import { useTranslation } from 'react-i18next';
import { t } from 'i18next';
import { PluginSettings } from '../plugin-settings';
import { useSchemasQuery } from '@/api';
import { UpdateOrderGet } from '../update-order-get';

export const useSetup = () => {
  /* Context state */
  const { error } = useNotify();
  const { i18n } = useTranslation();

  const [
    setOperatorIdConfig,
    setDomainAccess,
    setUserId,
    setJWT,
    isUpdateDiff,
    storedUserId,
  ] = useStore(({ config: c }) => [
    c.setOperatorId,
    c.setDomainAccess,
    c.setUserId,
    c.setJWT,
    c.isUpdateDiff,
    c.userId,
  ]);
  const [step, setCurrentStep, setOperatorId] = useSetupStore((s) => [
    s.currentStep,
    s.setCurrentStep,
    s.setOperatorId,
  ]);
  const [opId, setOpId] = useState<number>();

  const { search } = useLocation();

  const params = new URLSearchParams(search);
  const userIdParam = params.get('userId') ?? undefined;
  const jwtParam = params.get('token') ?? undefined;

  const { data: operatorId } = useFetchOperatorIdByUserId(
    userIdParam ?? storedUserId,
  );
  const { data: domains } = useDomainsbyOperatorId(operatorId?.toString());
  const { data: language } = useLanguagebyOperatorId(operatorId?.toString());
  const { data: schemas = [], isFetched: schemasFetched } = useSchemasQuery({
    status: 'active',
  });

  /**
   * Handlers
   */
  const init = () => {
    const { userId: persistedUserId, JWT: persistedJWT } =
      useStore.getState().config;
    sessionStorage.clear();

    const userId = userIdParam ?? persistedUserId;
    if (userId === undefined) {
      error(t('message.error.noUserIdInUrl'));
    } else {
      setUserId(userId);
    }

    const jwt = jwtParam ?? persistedJWT;
    if (jwt === undefined) {
      if (!import.meta.env.DEV) {
        error(t('message.error.noJWTInUrl'));
      }
    } else {
      setJWT(jwt);
    }
  };

  useEffect(() => {
    if (!language) return;
    i18n.changeLanguage(language);
  }, [language]);

  useEffect(() => {
    if (userIdParam === undefined) return;
    setUserId(userIdParam);
  }, [setUserId, userIdParam]);

  useEffect(() => {
    if (jwtParam === undefined) return;
    setJWT(jwtParam);
  }, [setJWT, jwtParam]);

  useEffect(() => {
    if (!opId) return;
    if (!domains || domains.length === 0) return;
    if (!schemasFetched) return;

    assertNotEmpty(domains, 'No domains available for the current operator.');

    const availableDomains = new Set(schemas.map((schema) => schema.domain));
    const validDomains = domains.filter((domain) =>
      availableDomains.has(domain),
    );
    const invalidDomains = domains.filter(
      (domain) => !availableDomains.has(domain),
    );

    if (invalidDomains.length > 0) {
      error(
        t('message.error.invalidDomains', {
          domains: invalidDomains.join(', '),
        }),
      );
    }

    setDomainAccess(validDomains);
  }, [domains, error, opId, schemas, schemasFetched, setDomainAccess]);

  useEffect(() => {
    if (operatorId === undefined) return;

    if (operatorId) {
      setOpId(operatorId);
      setOperatorId(operatorId);
      setOperatorIdConfig(operatorId);
    }
  }, [operatorId]);

  const handlePrevious = () => {
    setCurrentStep(step - 1);
  };

  const renderStep = () => {
    switch (step) {
      case 0:
        return DomainSelect;
      case 1:
        return TemplateSelect;
      case 2:
        return isUpdateDiff ? UpdateOrderGet : FileSelect;
      case 3:
        return PluginSettings;
      case 4:
        return PluginView;
      default:
        throw new Error('Invalid step');
    }
  };

  const handleLanguageChange = (event: React.MouseEvent<HTMLButtonElement>) => {
    const language = event.currentTarget.getAttribute('data-lang');
    if (language) {
      i18n.changeLanguage(language);
    }
  };

  return {
    opId,
    init,
    step,
    handlePrevious,
    renderStep,
    handleLanguageChange,
  };
};
